/**
 * ISG Customer Certificates — internationalisation.
 *
 * A thin adapter over the estate's shared engine in `@isg/ui-kit`, modelled on
 * isg-induction's. Pages import `t` / `getLocale` from here, never from ui-kit
 * directly, so the app's catalogue is guaranteed to be registered first.
 *
 * This app's catalogues are NESTED (`{"nav": {"dashboard": "..."}}`).
 * de / cs / en-US live in ./locales/ and are written by the translator
 * (ui-kit/scripts/translate-catalogue.py) — never by hand.
 */
import {
  registerAppMessages, startAppI18n,
  t, getLocale, setLocale, onLocaleChange, getAvailableLocales,
} from '@isg/ui-kit';

import enGB from './en-GB.json';

// ⚠️ AT MODULE SCOPE — any t() evaluated during the import graph would
// otherwise run before boot and render its own key, in every language.
registerAppMessages('en-GB', enGB);

export { t, getLocale, setLocale, onLocaleChange, getAvailableLocales };

/**
 * Boot i18n with the signed-in user's stored locale (`user.locale`), or null
 * when signed out. Call after the session check and BEFORE the shell renders.
 * (Named initI18n like isg-induction's adapter; it wraps startAppI18n.)
 */
export function initI18n(user = null) {
  return startAppI18n({
    base: enGB,
    // Literal template so Vite can glob ./locales/*.json at build time.
    load: (locale) => import(`./locales/${locale}.json`),
    user,
  });
}

/**
 * A data value (status / action / event slug) translated through a key map,
 * with a readable fallback for a slug the catalogue does not know yet:
 *
 *   enumLabel(t(`certificates.status.${c.status}`), c.status)
 *
 * `t()` returns the KEY itself when it is missing (never '' — so `||` cannot
 * fall back), and that key ends in `.<slug>`; that is what is detected here.
 * The t() call stays at the call site so the audit sees the key prefix.
 */
export function enumLabel(translated, slug) {
  if (slug == null || slug === '') return '';
  const s = String(slug);
  if (translated.endsWith(`.${s}`) && !/\s/.test(translated)) return s.replace(/_/g, ' ');
  return translated;
}
